export interface QuickQuoteRecoveryScope {
  companyId: string;
  locationId: string | null;
}

interface StoredPointer {
  id: string;
  expiresAt: number;
  /** When this pointer was written. Absent on pointers stored before it was. */
  savedAt?: number;
}

const PENDING_TTL_MS = 2 * 24 * 60 * 60_000;
const ACTIVE_TTL_MS = 30 * 24 * 60 * 60_000;

/**
 * How long a create is given to land before an empty lookup counts as proof.
 *
 * The pointer is written before the create is sent, and the server inserts the
 * operation row inside that request — so a recovery lookup can reach the server
 * first and find nothing while the create is still on its way. Retiring the
 * pointer on that answer would let a second paid parse start beside the first.
 * Absence is only proof once the create has had this long to arrive.
 */
const PENDING_SETTLE_MS = 2 * 60_000;

function storageKey(kind: 'pending' | 'active', scope: QuickQuoteRecoveryScope): string {
  return `blastoclean.quick-quote.${kind}.v1:${scope.companyId}:${scope.locationId ?? 'company'}`;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readPointer(kind: 'pending' | 'active', scope: QuickQuoteRecoveryScope): StoredPointer | null {
  const target = storage();
  if (!target) return null;
  const key = storageKey(kind, scope);
  try {
    const parsed = JSON.parse(target.getItem(key) ?? 'null') as Partial<StoredPointer> | null;
    if (
      !parsed ||
      typeof parsed.id !== 'string' ||
      parsed.id.length < 8 ||
      typeof parsed.expiresAt !== 'number' ||
      !Number.isFinite(parsed.expiresAt) ||
      parsed.expiresAt <= Date.now()
    ) {
      target.removeItem(key);
      return null;
    }
    return {
      id: parsed.id,
      expiresAt: parsed.expiresAt,
      // A pointer written before this field existed is old enough by
      // definition: its create was sent in an earlier page load.
      ...(typeof parsed.savedAt === 'number' && Number.isFinite(parsed.savedAt) ? { savedAt: parsed.savedAt } : {}),
    };
  } catch {
    try {
      target.removeItem(key);
    } catch {
      // Storage is a recovery aid. A browser policy refusing cleanup must not
      // stop a quote from being created in the current tab.
    }
    return null;
  }
}

function write(kind: 'pending' | 'active', scope: QuickQuoteRecoveryScope, id: string, ttlMs: number): void {
  const target = storage();
  if (!target) return;
  try {
    // No prompt, customer details, address or parsed facts are written here.
    // The server is the only recovery source; the browser keeps opaque ids and
    // the minimum lifecycle metadata needed to expire them.
    const now = Date.now();
    target.setItem(storageKey(kind, scope), JSON.stringify({ id, expiresAt: now + ttlMs, savedAt: now }));
  } catch {
    // The live request still works when storage is unavailable.
  }
}

function clear(kind: 'pending' | 'active', scope: QuickQuoteRecoveryScope, expectedId: string): void {
  const target = storage();
  if (!target) return;
  const key = storageKey(kind, scope);
  try {
    const parsed = JSON.parse(target.getItem(key) ?? 'null') as Partial<StoredPointer> | null;
    // Another tab may have installed a newer pointer since this tab began its
    // request. Never let the older tab erase the newer tab's recovery path.
    if (parsed?.id === expectedId) target.removeItem(key);
  } catch {
    // An unreadable pointer cannot be proven to be ours, so leave it alone.
  }
}

export function loadPendingQuickQuote(scope: QuickQuoteRecoveryScope): string | null {
  return readPointer('pending', scope)?.id ?? null;
}

/**
 * Whether an empty lookup for this pending pointer can be believed yet.
 *
 * A pointer this device wrote moments ago may name a create that has not
 * reached the server, so "no such request" is not yet proof it will never
 * exist. One that is already gone counts as settled: there is nothing left to
 * protect.
 */
export function pendingQuickQuoteIsSettled(scope: QuickQuoteRecoveryScope, expectedId: string): boolean {
  const pointer = readPointer('pending', scope);
  if (!pointer || pointer.id !== expectedId) return true;
  return pointer.savedAt === undefined || Date.now() - pointer.savedAt >= PENDING_SETTLE_MS;
}

export function rememberPendingQuickQuote(scope: QuickQuoteRecoveryScope, clientRequestId: string): void {
  write('pending', scope, clientRequestId, PENDING_TTL_MS);
}

export function clearPendingQuickQuote(scope: QuickQuoteRecoveryScope, expectedClientRequestId: string): void {
  clear('pending', scope, expectedClientRequestId);
}

export function loadActiveQuickQuote(scope: QuickQuoteRecoveryScope): string | null {
  return readPointer('active', scope)?.id ?? null;
}

export function rememberActiveQuickQuote(scope: QuickQuoteRecoveryScope, sessionId: string): void {
  write('active', scope, sessionId, ACTIVE_TTL_MS);
}

export function clearActiveQuickQuote(scope: QuickQuoteRecoveryScope, expectedSessionId: string): void {
  clear('active', scope, expectedSessionId);
}
